import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { api, useApi } from '../api/queries'
import type { components } from '../api/schema'
import type { BanReplay, ConstructorStats, DriverStats } from '../api/types'
import { matches } from '../components/CareerTables'
import {
  banQuery,
  banSearch,
  isEmpty,
  listSavedBans,
  readBans,
  removeSavedBans,
  saveBans,
  writeBans,
  type Bans,
} from '../lib/bans'
import { UniverseConstructorPage, UniverseDriverPage } from './PeoplePages'
import { UniverseRacePage } from './RacePage'
import {
  ReplayChampionshipPage,
  ReplayConstructorsPage,
  ReplayDriversPage,
  ReplaySeasonPage,
  universeOf,
  type Source,
} from './ReplayPages'

type GrandPrixPage = components['schemas']['Page_GrandPrixSummary_']

/** A name per slug, for every driver, constructor and Grand Prix there is to ban. */
type Option = { slug: string; name: string; detail?: string | undefined }

function useBans() {
  const [params, setParams] = useSearchParams()
  const set = (bans: Bans) => setParams(writeBans(params, bans), { replace: true })
  return { bans: readBans(params), search: banSearch(params), set }
}

function useOptions() {
  const drivers = useApi<DriverStats[]>(['stats', 'drivers'], () =>
    api.GET('/api/v1/stats/drivers', {}),
  )
  const constructors = useApi<ConstructorStats[]>(['stats', 'constructors'], () =>
    api.GET('/api/v1/stats/constructors', {}),
  )
  const grandsPrix = useApi<GrandPrixPage>(['grands-prix'], () =>
    api.GET('/api/v1/grands-prix', { params: { query: { limit: 200 } } }),
  )
  const span = (first?: number | null, last?: number | null) =>
    first ? (first === last ? `${first}` : `${first}–${last}`) : undefined
  return {
    drivers: (drivers.data ?? []).map((row) => ({
      slug: row.driver.slug,
      name: row.driver.name,
      detail: span(row.first_year, row.last_year),
    })),
    constructors: (constructors.data ?? []).map((row) => ({
      slug: row.constructor.slug,
      name: row.constructor.name,
      detail: span(row.first_year, row.last_year),
    })),
    grandsPrix: (grandsPrix.data?.items ?? []).map((row) => ({
      slug: row.slug,
      name: row.name,
    })),
  }
}

function useReplay(bans: Bans) {
  const query = banQuery(bans)
  return useApi<BanReplay>(
    ['ban', query],
    () => api.GET('/api/v1/ban', { params: { query } }),
    {
      enabled: !isEmpty(bans),
    },
  )
}

const FIELDS = [
  { field: 'drivers', label: 'Drivers' },
  { field: 'constructors', label: 'Constructors' },
  { field: 'grandsPrix', label: 'Grands Prix' },
] as const

/** The selection screen: who and what never existed. */
export function BanSelectPage() {
  const { bans, search, set } = useBans()
  const options = useOptions()

  return (
    <section>
      <h1>Ban from history</h1>
      <p className="prose muted">
        Choose drivers, constructors and Grands Prix. Every championship is then decided again
        as if they had never existed: a banned driver or team is taken out of every race and the
        field behind moves up; a banned Grand Prix is struck from every season it was run in.
      </p>
      <div className="ban">
        {FIELDS.map(({ field, label }) => (
          <Picker
            key={field}
            label={label}
            options={options[field]}
            chosen={bans[field]}
            onChange={(slugs) => set({ ...bans, [field]: slugs })}
          />
        ))}
      </div>
      {isEmpty(bans) ? null : (
        <p className="prose">
          <Link to={`/ban/championship${search}`}>See the champions →</Link>
        </p>
      )}
      <Saved bans={bans} options={options} onPick={set} />
    </section>
  )
}

/** Bans kept under a name in this browser, to pick again later. */
function Saved({
  bans,
  options,
  onPick,
}: {
  bans: Bans
  options: Record<(typeof FIELDS)[number]['field'], Option[]>
  onPick: (bans: Bans) => void
}) {
  const [name, setName] = useState('')
  const [saved, setSaved] = useState(listSavedBans)
  const [refused, setRefused] = useState(false)
  const keep = (ok: boolean) => {
    setRefused(!ok)
    setSaved(listSavedBans())
  }
  const current = writeBans(new URLSearchParams(), bans).toString()
  const describe = (entry: Bans) =>
    FIELDS.flatMap(({ field }) => {
      const known = new Map(options[field].map((option) => [option.slug, option.name]))
      return entry[field].map((slug) => known.get(slug) ?? slug)
    }).join(', ')

  return (
    <>
      <h2>Saved bans</h2>
      <form
        className="search"
        onSubmit={(event) => {
          event.preventDefault()
          keep(saveBans(name.trim(), bans))
          setName('')
        }}
      >
        <input
          type="text"
          value={name}
          aria-label="Name these bans"
          placeholder="Name these bans"
          onChange={(event) => setName(event.target.value)}
        />
        <button type="submit" disabled={isEmpty(bans) || !name.trim()}>
          Save
        </button>
      </form>
      {refused ? (
        <p className="scale__problem" role="alert">
          This browser would not store them.
        </p>
      ) : null}
      {Object.keys(saved).length ? (
        <ul className="saved">
          {Object.entries(saved)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([title, entry]) => (
              <li key={title}>
                <button
                  type="button"
                  className="link-button"
                  aria-current={writeBans(new URLSearchParams(), entry).toString() === current}
                  onClick={() => onPick(entry)}
                >
                  {title}
                </button>{' '}
                <span className="muted">{describe(entry)}</span>{' '}
                <button
                  type="button"
                  className="link-button"
                  aria-label={`Delete ${title}`}
                  onClick={() => keep(removeSavedBans(title))}
                >
                  Delete
                </button>
              </li>
            ))}
        </ul>
      ) : (
        <p className="muted">None yet. They are kept in this browser only.</p>
      )}
    </>
  )
}

function Picker({
  label,
  options,
  chosen,
  onChange,
}: {
  label: string
  options: Option[]
  chosen: string[]
  onChange: (slugs: string[]) => void
}) {
  // Typing is not a choice, so it stays out of the URL.
  const [query, setQuery] = useState('')
  const banned = new Set(chosen)
  const shown = options.filter((option) => matches(option.name, query))
  const kept = shown.filter((option) => !banned.has(option.slug)).length
  const names = new Map(options.map((option) => [option.slug, option.name]))
  // Checked means still in history, so everything starts checked and unchecking bans.
  const toggle = (slug: string) =>
    onChange(banned.has(slug) ? chosen.filter((item) => item !== slug) : [...chosen, slug])
  // The search narrows what "All" reaches: search "Ferrari", untick All, and only those go.
  const toggleAll = () => {
    const slugs = new Set(shown.map((option) => option.slug))
    onChange(
      kept === shown.length ? [...chosen, ...slugs] : chosen.filter((item) => !slugs.has(item)),
    )
  }

  return (
    <fieldset className="ban__picker">
      <legend>{label}</legend>
      {chosen.length ? (
        <ul className="ban__chosen">
          {chosen.map((slug) => (
            <li key={slug}>
              <button
                type="button"
                className="ban__chip"
                aria-label={`Unban ${names.get(slug) ?? slug}`}
                onClick={() => toggle(slug)}
              >
                {names.get(slug) ?? slug} <span aria-hidden="true">×</span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">None banned.</p>
      )}
      <div className="search">
        <input
          type="search"
          value={query}
          aria-label={`Search ${label.toLowerCase()}`}
          placeholder={`Search ${label.toLowerCase()}`}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      <label className="ban__all">
        <input
          type="checkbox"
          checked={shown.length > 0 && kept === shown.length}
          ref={(box) => {
            if (box) box.indeterminate = kept > 0 && kept < shown.length
          }}
          disabled={!shown.length}
          onChange={toggleAll}
        />
        {query.trim() ? `All ${shown.length} found` : 'All'}
      </label>
      <ul className="ban__list">
        {shown.map((option) => (
          <li key={option.slug}>
            <label>
              <input
                type="checkbox"
                checked={!banned.has(option.slug)}
                onChange={() => toggle(option.slug)}
              />
              {option.name}
              {option.detail ? <span className="muted"> · {option.detail}</span> : null}
            </label>
          </li>
        ))}
      </ul>
    </fieldset>
  )
}

/** What is banned, named, with the way back to change it. Nothing banned: the way there. */
function Banned({ bans, search }: { bans: Bans; search: string }) {
  const options = useOptions()
  if (isEmpty(bans)) {
    return (
      <p className="prose">
        Nothing is banned yet. <Link to="/ban">Choose who and what never existed →</Link>
      </p>
    )
  }
  const names = FIELDS.flatMap(({ field }) => {
    const known = new Map(options[field].map((option) => [option.slug, option.name]))
    return bans[field].map((slug) => known.get(slug) ?? slug)
  })
  return (
    <p className="prose">
      <span className="muted">Banned: </span>
      {names.join(', ')}. <Link to={`/ban${search}`}>Change</Link>
    </p>
  )
}

const BANS: Source = {
  path: '/ban',
  suffix: 'with the bans',
  use: function useBanSource() {
    const { bans, search } = useBans()
    return {
      chosen: !isEmpty(bans),
      search,
      replay: useReplay(bans),
      summary: <Banned bans={bans} search={search} />,
      driverSeasons: (slug) => [
        ['ban', 'driver-seasons', slug, banQuery(bans)],
        () =>
          api.GET('/api/v1/ban/drivers/{slug}/seasons', {
            params: { path: { slug }, query: banQuery(bans) },
          }),
      ],
      seasonQuery: (races) => {
        const rounds = races
          .filter((race) => bans.grandsPrix.includes(race.grand_prix.slug))
          .map((race) => race.round)
        const { exclude_drivers, exclude_constructors } = banQuery(bans)
        return {
          ...(exclude_drivers ? { exclude_drivers } : {}),
          ...(exclude_constructors ? { exclude_constructors } : {}),
          ...(rounds.length ? { exclude_rounds: rounds.join(',') } : {}),
        }
      },
    }
  },
}

export const BanChampionshipPage = () => <ReplayChampionshipPage source={BANS} />
export const BanDriversPage = () => <ReplayDriversPage source={BANS} />
export const BanConstructorsPage = () => <ReplayConstructorsPage source={BANS} />
export const BanSeasonPage = () => <ReplaySeasonPage source={BANS} />

const BANS_UNIVERSE = universeOf(BANS)
export const BanDriverPage = () => <UniverseDriverPage universe={BANS_UNIVERSE} />
export const BanConstructorPage = () => <UniverseConstructorPage universe={BANS_UNIVERSE} />
export const BanRacePage = () => <UniverseRacePage universe={BANS_UNIVERSE} />
